// Survey camera: one live camera sheet for a single photo, Take Photos, or a Photo Run.
// Words: Collect words sections 3.1 and 4; IEA_PHOTO_FLOW_WORDS.md (Robert 2026-10-16).
// * The back camera stays open between shots, so a run is one tap per photo.
// * A big label says what the next shot becomes: "Front · 1 of 3". Never a guess.
// * The last shot sits in the bottom left corner, like the iPhone Camera. Tap it: Retake, Use Photo, or Delete.
// * A shot is a JPEG from the camera's full frame, sized for reading a nameplate at the hotel.
// If the camera cannot open, the iPad's own camera picker takes the photo instead.

export interface Shot {
  blob: Blob;
  contentType: string;
}

export interface CameraSheet {
  /** The big label: what the next shot becomes. */
  label(text: string): void;
  /** Back is greyed out at the first photo. */
  canBack(on: boolean): void;
  close(): void;
}

export interface CameraOptions {
  title: string;
  label: string;
  /** Take Photos or Photo Run: Back and Skip show, and the camera stays open after a shot. */
  run: boolean;
  onShot: (shot: Shot) => void;
  onSkip: () => void;
  onBack?: () => void;
  onDone: () => void;
  /** The last shot's Retake: the caller steps back so the next shot replaces it. */
  onRetakeLast?: () => void;
  /** The last shot's Delete. */
  onDeleteLast?: () => void;
}

const LONG_SIDE = 2400;

async function frameToJpeg(video: HTMLVideoElement): Promise<Shot | null> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const scale = Math.min(1, LONG_SIDE / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.round(w * scale);
  c.height = Math.round(h * scale);
  c.getContext("2d")?.drawImage(video, 0, 0, c.width, c.height);
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, "image/jpeg", 0.85));
  return blob ? { blob, contentType: "image/jpeg" } : null;
}

export function openCamera(o: CameraOptions): CameraSheet {
  let stream: MediaStream | null = null;
  let closed = false;
  let lastUrl = "";
  const el = document.createElement("div");
  el.className = "sv-sheet cl-cam";
  el.innerHTML = `<div class="cl-camhd"><b>${escapeHtml(o.title)}</b><button class="cam-top" type="button" data-cam="done">${o.run ? "Done" : "Cancel"}</button></div>
    <div class="sv-cam cl-camview"><video playsinline muted></video><div class="sv-camtx" id="cl-camtx">Starting the camera…</div></div>
    <div class="cam-shoot"><small>Shoot</small><b id="cl-camlabel">${escapeHtml(o.label)}</b></div>
    <div class="cam-ft">
      <button class="cam-last none" type="button" data-cam="last" aria-label="Check the last shot" disabled></button>
      <div class="cam-mid"><button class="cam-shutter" type="button" data-cam="shoot" aria-label="Take Photo"></button>
        <label class="sv-btn line" hidden id="cl-campick">Use Camera App<input type="file" accept="image/*" capture="environment" hidden></label></div>
      <div class="cam-side">${o.run ? `${o.onBack ? '<button class="cam-top" type="button" data-cam="back" disabled>Back</button>' : ""}<button class="cam-top" type="button" data-cam="skip">Skip</button>` : ""}</div>
    </div>
    <div class="cam-review" hidden><img alt="Last shot"><div class="cam-rvft"><button class="cam-top" type="button" data-cam="rv-retake">Retake</button><button class="cam-use" type="button" data-cam="rv-use">Use Photo</button><button class="cam-top red" type="button" data-cam="rv-trash" aria-label="Delete">Delete</button></div>
      <div class="cam-confirm" hidden><b>Delete this photo?</b><button class="cam-del" type="button" data-cam="rv-del">Delete Photo</button><button class="cam-top" type="button" data-cam="rv-cancel">Cancel</button></div></div>`;
  document.body.appendChild(el);
  const video = el.querySelector("video")!;
  const tx = el.querySelector<HTMLElement>("#cl-camtx")!;
  const pick = el.querySelector<HTMLLabelElement>("#cl-campick")!;
  const last = el.querySelector<HTMLButtonElement>('[data-cam="last"]')!;
  const review = el.querySelector<HTMLElement>(".cam-review")!;
  const confirmBox = el.querySelector<HTMLElement>(".cam-confirm")!;

  const stop = () => {
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
  };
  const setLast = (blob: Blob | null) => {
    if (lastUrl) URL.revokeObjectURL(lastUrl);
    lastUrl = blob ? URL.createObjectURL(blob) : "";
    last.style.backgroundImage = lastUrl ? `url("${lastUrl}")` : "";
    last.classList.toggle("none", !lastUrl);
    last.disabled = !lastUrl;
  };
  const close = () => {
    if (closed) return;
    closed = true;
    stop();
    setLast(null);
    el.remove();
  };
  const took = (shot: Shot) => {
    if (o.run) setLast(shot.blob);
    o.onShot(shot);
  };

  void (async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("no camera");
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 3264 }, height: { ideal: 2448 } }, audio: false });
      if (closed) return stop();
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      tx.textContent = "";
    } catch {
      tx.textContent = "The camera did not open here. Use the iPad camera instead.";
      pick.hidden = false;
      el.querySelector<HTMLElement>('[data-cam="shoot"]')!.hidden = true;
    }
  })();

  el.addEventListener("click", async (ev) => {
    const act = (ev.target as HTMLElement).closest<HTMLElement>("[data-cam]")?.dataset.cam;
    if (act === "shoot") {
      const shot = await frameToJpeg(video);
      if (!shot) {
        tx.textContent = "The camera is not ready yet.";
        return;
      }
      tx.textContent = "Saved";
      setTimeout(() => {
        if (tx.textContent === "Saved") tx.textContent = "";
      }, 700);
      took(shot);
    } else if (act === "skip") o.onSkip();
    else if (act === "back") o.onBack?.();
    else if (act === "done") {
      close();
      o.onDone();
    } else if (act === "last" && lastUrl) {
      review.querySelector("img")!.src = lastUrl;
      confirmBox.hidden = true;
      review.hidden = false;
    } else if (act === "rv-use") review.hidden = true;
    else if (act === "rv-retake") {
      review.hidden = true;
      setLast(null);
      o.onRetakeLast?.();
    } else if (act === "rv-trash") confirmBox.hidden = false;
    else if (act === "rv-cancel") confirmBox.hidden = true;
    else if (act === "rv-del") {
      review.hidden = true;
      setLast(null);
      o.onDeleteLast?.();
    }
  });
  pick.querySelector("input")!.addEventListener("change", (ev) => {
    const input = ev.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = "";
    if (f) took({ blob: f, contentType: f.type === "image/png" ? "image/png" : "image/jpeg" });
  });

  return {
    label: (text) => {
      const l = el.querySelector("#cl-camlabel");
      if (l) l.textContent = text;
    },
    canBack: (on) => {
      const b = el.querySelector<HTMLButtonElement>('[data-cam="back"]');
      if (b) b.disabled = !on;
    },
    close,
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
